import React, { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import { useNavigate, useSearchParams } from "react-router-dom"
import {Search, Tag, Mail, Download, Eye, RefreshCw, X} from 'lucide-react'

interface Customer {
  _id: string
  userId: string
  email: string
  userName: string
  phone?: string
  memberLevel: "LV1" | "LV2" | "LV3" | "LV4" | "LV5"
  status: "active" | "inactive" | "blacklist"
  totalSpent: number
  orderCount: number
  points: number
  tags?: string[]
  preferences?: {
    roastLevel?: string
    flavorNotes?: string[]
    favoriteOrigins?: string[]
  }
  referralCode?: string
  referredBy?: string
  lastPurchaseAt?: string
  createdAt: string
  updatedAt: string
}

const levelColors: Record<string, string> = {
  LV1: "bg-gray-600",
  LV2: "bg-blue-600",
  LV3: "bg-purple-600",
  LV4: "bg-orange-600",
  LV5: "bg-yellow-600"
}

const CustomerManager: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [customers, setCustomers] = useState<Customer[]>([])
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterLevel, setFilterLevel] = useState<string>("all")
  const [filterStatus, setFilterStatus] = useState<string>("all")
  const [filterTags, setFilterTags] = useState<string[]>([])
  const [showTagFilter, setShowTagFilter] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [showDetail, setShowDetail] = useState(false)
  const [isFixing, setIsFixing] = useState(false)
  const [fixResult, setFixResult] = useState<any>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editPhone, setEditPhone] = useState("")
  const [editMemberLevel, setEditMemberLevel] = useState<"LV1" | "LV2" | "LV3" | "LV4" | "LV5">("LV1")
  const [editTags, setEditTags] = useState<string[]>([])
  const [availableTags, setAvailableTags] = useState<Array<{ _id: string; name: string }>>([])
  const [isSaving, setIsSaving] = useState(false)
  const [newTagName, setNewTagName] = useState("")
  const [isCreatingTag, setIsCreatingTag] = useState(false)

  useEffect(() => {
    fetchCustomers()
    fetchAvailableTags()
    
    // 从 URL 参数读取标签筛选
    const tagParam = searchParams.get('tag')
    if (tagParam) {
      setFilterTags([tagParam])
    }
  }, [])

  useEffect(() => {
    applyFilters()
  }, [customers, searchTerm, filterLevel, filterStatus, filterTags])

  const fetchCustomers = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.customers.list({
        sort: { createdAt: -1 }
      })
      setCustomers(list)
    } catch (error) {
      console.error("Failed to fetch customers:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchAvailableTags = async () => {
    try {
      const { list } = await lumi.entities.customer_tags.list({
        sort: { name: 1 }
      })
      setAvailableTags(list || [])
    } catch (error) {
      console.error("Failed to fetch tags:", error)
    }
  }

  const applyFilters = () => {
    let filtered = customers

    if (searchTerm) {
      filtered = filtered.filter(
        (c) =>
          c.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.phone?.includes(searchTerm)
      )
    }

    if (filterLevel !== "all") {
      filtered = filtered.filter((c) => c.memberLevel === filterLevel)
    }

    if (filterStatus !== "all") {
      filtered = filtered.filter((c) => c.status === filterStatus)
    }

    if (filterTags.length > 0) {
      filtered = filtered.filter((c) => 
        c.tags && c.tags.some(tag => filterTags.includes(tag))
      )
    }

    setFilteredCustomers(filtered)
  }

  const exportToCSV = () => {
    const headers = ["姓名", "Email", "手機", "會員等級", "累積消費", "點數", "註冊日期"]
    const rows = filteredCustomers.map((c) => [
      c.userName,
      c.email,
      c.phone || "",
      c.memberLevel,
      c.totalSpent,
      c.points,
      new Date(c.createdAt).toLocaleDateString("zh-TW")
    ])

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(","))
    ].join("\n")

    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = `會員資料_${new Date().toISOString().split("T")[0]}.csv`
    link.click()
  }

  const viewDetail = async (customer: Customer) => {
    setSelectedCustomer(customer)
    setShowDetail(true)
    setIsEditing(false)
    setEditPhone(customer.phone || "")
    setEditMemberLevel(customer.memberLevel)
    setEditTags(customer.tags || [])
    await fetchAvailableTags()  // 每次打开详情时重新加载标签
  }

  const viewCustomerOrders = (email: string) => {
    navigate(`/admin/orders?email=${encodeURIComponent(email)}`)
  }

  const fixCustomerSpending = async () => {
    if (!confirm("確定要重新計算所有會員的累積消費和積分嗎？\n\n此操作將：\n1. 重新統計所有 paid/completed 訂單\n2. 更新會員 totalSpent、orderCount、points（1元=1分）\n3. 修正訂單 spentRecorded 標記\n4. 自動刷新會員列表")) {
      return
    }

    setIsFixing(true)
    setFixResult(null)

    try {
      const result = await lumi.functions.invoke("fixCustomerSpending", {
        method: "POST"
      })

      console.log("修復結果：", result)
      setFixResult(result)
      
      // 重新載入會員資料
      await fetchCustomers()
      
      // 生成修復報告
      const summary = result.report?.summary || {}
      const details = result.report?.customerDetails || []
      
      let reportMessage = `✅ 修復完成！\n\n統計數據：\n- 總訂單數：${summary.totalOrders || 0}\n- 總會員數：${summary.totalCustomers || 0}\n- 有訂單會員：${summary.affectedCustomers || 0}\n- 更新會員數：${summary.updatedCustomers || 0}\n- 更新訂單數：${summary.updatedOrders || 0}\n\n`
      
      if (details.length > 0) {
        reportMessage += `前 5 位會員積分詳情：\n`
        details.slice(0, 5).forEach((detail: any) => {
          reportMessage += `- ${detail.email}: NT$ ${detail.totalSpent?.toLocaleString()} = ${detail.points} 點\n`
        })
      }
      
      reportMessage += `\n所有會員的 Wallet Points 已同步更新為累積消費金額（1元=1分）。`
      
      alert(reportMessage)
    } catch (error) {
      console.error("修復失敗：", error)
      alert(`❌ 修復失敗：${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setIsFixing(false)
    }
  }

  const savePhone = async () => {
    if (!selectedCustomer) return

    setIsSaving(true)
    try {
      // 计算标签变化
      const oldTags = selectedCustomer.tags || []
      const addedTags = editTags.filter(t => !oldTags.includes(t))
      const removedTags = oldTags.filter(t => !editTags.includes(t))

      // 更新会员信息
      await lumi.entities.customers.update(selectedCustomer._id, {
        phone: editPhone,
        memberLevel: editMemberLevel,
        tags: editTags
      })

      // 更新标签使用次数
      for (const tagName of addedTags) {
        const tag = availableTags.find(t => t.name === tagName)
        if (tag) {
          const tagData = await lumi.entities.customer_tags.get(tag._id)
          if (tagData) {
            await lumi.entities.customer_tags.update(tag._id, {
              usageCount: (tagData.usageCount || 0) + 1,
              updatedAt: new Date().toISOString()
            })
          }
        }
      }

      for (const tagName of removedTags) {
        const tag = availableTags.find(t => t.name === tagName)
        if (tag) {
          const tagData = await lumi.entities.customer_tags.get(tag._id)
          if (tagData && tagData.usageCount > 0) {
            await lumi.entities.customer_tags.update(tag._id, {
              usageCount: tagData.usageCount - 1,
              updatedAt: new Date().toISOString()
            })
          }
        }
      }

      // 更新本地状态
      const updatedCustomer = { ...selectedCustomer, phone: editPhone, memberLevel: editMemberLevel, tags: editTags }
      setCustomers(customers.map(c => 
        c._id === selectedCustomer._id ? updatedCustomer : c
      ))
      setSelectedCustomer(updatedCustomer)
      setIsEditing(false)
      
      alert("✅ 會員資料已更新")
    } catch (error) {
      console.error("保存失敗：", error)
      alert(`❌ 保存失敗：${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setIsSaving(false)
    }
  }

  const toggleTag = (tagName: string) => {
    if (editTags.includes(tagName)) {
      setEditTags(editTags.filter(t => t !== tagName))
    } else {
      setEditTags([...editTags, tagName])
    }
  }

  const createNewTag = async () => {
    const trimmedName = newTagName.trim()
    
    if (!trimmedName) {
      alert("请输入标签名称")
      return
    }

    // 检查是否已存在
    if (availableTags.some(tag => tag.name === trimmedName)) {
      alert("该标签已存在")
      return
    }

    setIsCreatingTag(true)
    try {
      const now = new Date().toISOString()
      const newTag = await lumi.entities.customer_tags.create({
        name: trimmedName,
        color: "#6b7280",
        usageCount: 0,
        creator: lumi.auth.user?.email || "system",
        createdAt: now,
        updatedAt: now
      })

      // 刷新标签列表
      await fetchAvailableTags()
      
      // 自动选中新创建的标签
      setEditTags([...editTags, trimmedName])
      
      // 清空输入框
      setNewTagName("")
      
      alert("✅ 标签创建成功并已选中")
    } catch (error) {
      console.error("创建标签失败:", error)
      alert("❌ 创建标签失败")
    } finally {
      setIsCreatingTag(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">載入中...</div>
      </div>
    )
  }

  return (
    <div className="p-8 h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-light tracking-wider mb-2">會員管理</h2>
          <p className="text-gray-400">管理前台會員資料與標籤</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fixCustomerSpending}
            disabled={isFixing}
            className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={20} className={isFixing ? "animate-spin" : ""} />
            {isFixing ? "修復中..." : "修復會員數據"}
          </button>
          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white hover:bg-green-700 transition-colors"
          >
            <Download size={20} />
            匯出 CSV
          </button>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="md:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="搜尋姓名、Email、手機..."
              className="w-full pl-10 pr-4 py-3 bg-gray-950 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
            />
          </div>
        </div>

        <div>
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
            className="w-full px-4 py-3 bg-gray-950 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
          >
            <option value="all">所有等級</option>
            <option value="LV1">LV1</option>
            <option value="LV2">LV2</option>
            <option value="LV3">LV3</option>
            <option value="LV4">LV4</option>
            <option value="LV5">LV5</option>
          </select>
        </div>

        <div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full px-4 py-3 bg-gray-950 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
          >
            <option value="all">所有狀態</option>
            <option value="active">啟用</option>
            <option value="inactive">停用</option>
            <option value="blacklist">黑名單</option>
          </select>
        </div>

        <div className="relative">
          <button
            onClick={() => setShowTagFilter(!showTagFilter)}
            className="w-full px-4 py-3 bg-gray-950 border border-gray-800 text-gray-100 hover:border-gray-600 transition-colors text-left flex items-center justify-between"
          >
            <span>
              {filterTags.length === 0 ? "所有標籤" : `已選 ${filterTags.length} 個標籤`}
            </span>
            <Tag size={16} className="text-gray-500" />
          </button>
          
          {showTagFilter && (
            <div className="absolute z-10 mt-1 w-full bg-gray-950 border border-gray-800 max-h-64 overflow-y-auto">
              <div className="p-2">
                {filterTags.length > 0 && (
                  <button
                    onClick={() => setFilterTags([])}
                    className="w-full px-3 py-2 text-sm text-gray-400 hover:bg-gray-900 transition-colors text-left mb-2 border-b border-gray-800"
                  >
                    ✕ 清除所有篩選
                  </button>
                )}
                {availableTags.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">尚無可用標籤</div>
                ) : (
                  availableTags.map((tag) => {
                    const isSelected = filterTags.includes(tag.name)
                    return (
                      <label
                        key={tag._id}
                        className="flex items-center px-3 py-2 hover:bg-gray-900 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFilterTags([...filterTags, tag.name])
                            } else {
                              setFilterTags(filterTags.filter(t => t !== tag.name))
                            }
                          }}
                          className="mr-3 w-4 h-4 bg-gray-900 border border-gray-700 checked:bg-blue-600 checked:border-blue-600 cursor-pointer"
                        />
                        <span className="text-sm text-gray-100">{tag.name}</span>
                      </label>
                    )
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="text-3xl font-light text-gray-100 mb-2">{customers.length}</div>
          <div className="text-sm text-gray-400">總會員數</div>
        </div>
        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="text-3xl font-light text-gray-100 mb-2">
            {customers.filter((c) => ["LV4", "LV5"].includes(c.memberLevel)).length}
          </div>
          <div className="text-sm text-gray-400">VIP 會員</div>
        </div>
        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="text-3xl font-light text-gray-100 mb-2">
            ${customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0).toLocaleString()}
          </div>
          <div className="text-sm text-gray-400">總消費金額</div>
        </div>
        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="text-3xl font-light text-gray-100 mb-2">
            {customers.filter((c) => {
              if (!c.lastPurchaseAt) return false
              const days = Math.floor((Date.now() - new Date(c.lastPurchaseAt).getTime()) / (1000 * 60 * 60 * 24))
              return days <= 30
            }).length}
          </div>
          <div className="text-sm text-gray-400">30 天內活躍</div>
        </div>
      </div>

      <div className="bg-gray-950 border border-gray-800 overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-gray-800">
            <tr className="text-left">
              <th className="px-6 py-4 text-sm font-light text-gray-400">會員</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">等級</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">累積消費</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">點數</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">標籤</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">最後購買</th>
              <th className="px-6 py-4 text-sm font-light text-gray-400">操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.map((customer) => (
              <tr key={customer._id} className="border-b border-gray-800 hover:bg-gray-900 transition-colors">
                <td className="px-6 py-4">
                  <div>
                    <div className="text-gray-100">{customer.userName}</div>
                    <div className="text-sm text-gray-500">{customer.email}</div>
                    {customer.phone && <div className="text-xs text-gray-600">{customer.phone}</div>}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 ${levelColors[customer.memberLevel]} text-white text-xs`}>
                    {customer.memberLevel}
                  </span>
                </td>
                <td className="px-6 py-4">
                  {(customer.totalSpent || 0) > 0 ? (
                    <button
                      onClick={() => viewCustomerOrders(customer.email)}
                      className="text-blue-400 hover:text-blue-300 underline transition-colors"
                    >
                      ${customer.totalSpent?.toLocaleString() || 0}
                    </button>
                  ) : (
                    <span className="text-gray-500">$0</span>
                  )}
                </td>
                <td className="px-6 py-4 text-gray-300">{customer.points || 0}</td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {customer.tags?.slice(0, 2).map((tag, idx) => (
                      <span key={idx} className="px-2 py-1 bg-gray-800 text-gray-300 text-xs">
                        {tag}
                      </span>
                    ))}
                    {(customer.tags?.length || 0) > 2 && (
                      <span className="px-2 py-1 bg-gray-800 text-gray-500 text-xs">
                        +{(customer.tags?.length || 0) - 2}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-400 text-sm">
                  {customer.lastPurchaseAt
                    ? new Date(customer.lastPurchaseAt).toLocaleDateString("zh-TW")
                    : "尚未購買"}
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => viewDetail(customer)}
                    className="p-2 hover:bg-gray-800 transition-colors"
                    title="查看詳情"
                  >
                    <Eye size={16} className="text-gray-400" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredCustomers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {searchTerm || filterLevel !== "all" || filterStatus !== "all" || filterTags.length > 0
              ? "無符合條件的會員"
              : "尚無會員資料"}
          </div>
        )}
      </div>

      {showDetail && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-950 border border-gray-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-xl font-light tracking-wider">會員詳情</h3>
              <div className="flex items-center gap-3">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm"
                  >
                    編輯
                  </button>
                ) : (
                  <>
                    <button
                      onClick={savePhone}
                      disabled={isSaving}
                      className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? "保存中..." : "保存"}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditing(false)
                        setEditPhone(selectedCustomer.phone || "")
                        setEditMemberLevel(selectedCustomer.memberLevel)
                        setEditTags(selectedCustomer.tags || [])
                      }}
                      disabled={isSaving}
                      className="px-4 py-2 bg-gray-700 text-white hover:bg-gray-600 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      取消
                    </button>
                  </>
                )}
                <button 
                  onClick={() => {
                    setShowDetail(false)
                    setIsEditing(false)
                  }} 
                  className="text-gray-400 hover:text-gray-200"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-400 mb-1">姓名</div>
                  <div className="text-gray-100">{selectedCustomer.userName}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">會員等級</div>
                  {isEditing ? (
                    <select
                      value={editMemberLevel}
                      onChange={(e) => setEditMemberLevel(e.target.value as "LV1" | "LV2" | "LV3" | "LV4" | "LV5")}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500"
                    >
                      <option value="LV1">LV1</option>
                      <option value="LV2">LV2</option>
                      <option value="LV3">LV3</option>
                      <option value="LV4">LV4</option>
                      <option value="LV5">LV5</option>
                    </select>
                  ) : (
                    <span className={`px-3 py-1 ${levelColors[selectedCustomer.memberLevel]} text-white text-xs`}>
                      {selectedCustomer.memberLevel}
                    </span>
                  )}
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Email</div>
                  <div className="text-gray-100">{selectedCustomer.email}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">手機</div>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editPhone}
                      onChange={(e) => setEditPhone(e.target.value)}
                      placeholder="請輸入手機號碼"
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500"
                    />
                  ) : (
                    <div className="text-gray-100">{selectedCustomer.phone || "未提供"}</div>
                  )}
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">累積消費</div>
                  {(selectedCustomer.totalSpent || 0) > 0 ? (
                    <button
                      onClick={() => viewCustomerOrders(selectedCustomer.email)}
                      className="text-blue-400 hover:text-blue-300 underline transition-colors"
                    >
                      ${selectedCustomer.totalSpent?.toLocaleString() || 0}
                    </button>
                  ) : (
                    <div className="text-gray-500">$0</div>
                  )}
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">目前點數</div>
                  <div className="text-gray-100">{selectedCustomer.points || 0}</div>
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-400 mb-2">會員標籤</div>
                {isEditing ? (
                  <div>
                    {/* 快速新增标签 */}
                    <div className="mb-3 p-4 bg-gray-900 border border-gray-800">
                      <div className="text-xs text-gray-500 mb-2">快速新增標籤：</div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newTagName}
                          onChange={(e) => setNewTagName(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              createNewTag()
                            }
                          }}
                          placeholder="輸入新標籤名稱（例如：VIP會員）"
                          className="flex-1 px-3 py-2 bg-gray-950 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500 text-sm"
                          disabled={isCreatingTag}
                        />
                        <button
                          onClick={createNewTag}
                          disabled={isCreatingTag || !newTagName.trim()}
                          className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {isCreatingTag ? "創建中..." : "+ 新增"}
                        </button>
                      </div>
                    </div>

                    {/* 从现有标签选择 */}
                    <div className="mb-3 p-4 bg-gray-900 border border-gray-800">
                      <div className="text-xs text-gray-500 mb-2">從標籤管理系統選擇（點擊添加/移除）：</div>
                      <div className="flex flex-wrap gap-2">
                        {availableTags.length === 0 ? (
                          <div className="text-sm text-gray-500">尚無可用標籤，請使用上方快速新增功能創建</div>
                        ) : (
                          availableTags.map((tag) => (
                            <button
                              key={tag._id}
                              onClick={() => toggleTag(tag.name)}
                              className={`px-3 py-1 text-sm transition-colors ${
                                editTags.includes(tag.name)
                                  ? "bg-blue-600 text-white hover:bg-blue-700"
                                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                              }`}
                            >
                              {tag.name}
                              {editTags.includes(tag.name) && (
                                <X size={14} className="inline-block ml-1" />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </div>

                    {/* 已选择标签预览 */}
                    <div className="mt-2">
                      <div className="text-xs text-gray-500 mb-1">已選擇標籤：</div>
                      <div className="flex flex-wrap gap-2">
                        {editTags.length === 0 ? (
                          <span className="text-sm text-gray-500">無</span>
                        ) : (
                          editTags.map((tag, idx) => (
                            <span key={idx} className="px-3 py-1 bg-blue-600 text-white text-sm">
                              {tag}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {selectedCustomer.tags && selectedCustomer.tags.length > 0 ? (
                      selectedCustomer.tags.map((tag, idx) => (
                        <span key={idx} className="px-3 py-1 bg-gray-800 text-gray-300 text-sm">
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-gray-500">無標籤</span>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-800">
                <div>
                  <div className="text-sm text-gray-400 mb-1">註冊日期</div>
                  <div className="text-gray-100 text-sm">
                    {new Date(selectedCustomer.createdAt).toLocaleString("zh-TW")}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">最後購買</div>
                  <div className="text-gray-100 text-sm">
                    {selectedCustomer.lastPurchaseAt
                      ? new Date(selectedCustomer.lastPurchaseAt).toLocaleString("zh-TW")
                      : "尚未購買"}
                  </div>
                </div>
              </div>


            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomerManager
